import { useCallback, useEffect, useMemo, useState } from 'react';
import { SHIFT_LABEL, TASK_KIND_LABEL, TASK_STATUSES, TASK_STATUS_LABEL, type AreaDto, type TaskDto, type TaskStatus } from '@app/shared';
import { api } from '../api';
import { useApp } from '../state';
import { Icon, plural, Seg } from '../ui';
import { areaPath } from './Panels';
import { AreaFilter, loadFilter, saveFilter, useAreaMatch } from './AreaFilter';
import { ShiftSheet, TaskEditor, TaskSheet } from './TaskSheets';

const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
export const shiftText = (t: Pick<TaskDto, 'shiftDate' | 'shiftNo'>) =>
  t.shiftDate ? `${dayFmt.format(new Date(`${t.shiftDate}T12:00:00`))}${t.shiftNo ? ` · ${SHIFT_LABEL[t.shiftNo]}` : ''}` : null;

const KIND_ICON: Record<TaskDto['kind'], string> = { production: 'factory', check: 'tasks', changeover: 'gear', other: 'log' };

/**
 * Вкладка «Задачи» — доска: Новые → Назначены → В работе → Готово.
 * Руководитель ставит задание на смену и задачи, назначает людей и сроки; исполнитель отмечает начало
 * (задача переходит «В работе») и завершение. Отчёты проверок лежат в «Готово».
 */
export function OtherTasks() {
  const { fail, me } = useApp();
  const [tasks, setTasks] = useState<TaskDto[] | null>(null);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [col, setCol] = useState<TaskStatus>(() => (loadFilter('board-col') as TaskStatus | null) ?? 'assigned');
  const [mine, setMine] = useState(() => loadFilter('board-mine') === '1' || !me.manager);
  const [open, setOpen] = useState<string | null>(null);
  const [shift, setShift] = useState(false);
  const [newTask, setNewTask] = useState(false);
  const [area, setAreaState] = useState<string | null>(() => loadFilter('board'));
  const setArea = (v: string | null) => {
    setAreaState(v);
    saveFilter('board', v);
  };
  const match = useAreaMatch(areas, area);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([api.tasks(), api.areas()]);
      setTasks(t);
      setAreas(a);
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const myId = me.employee?.id;
  const shown = useMemo(
    // Производство — на доске этапов; здесь проверки, переналадки и прочие задачи.
    () => (tasks ?? []).filter((t) => t.kind !== 'production' && match(t.areaId) && (!mine || t.assignees.some((a) => a.id === myId) || (!t.assignees.length && t.status === 'new'))),
    [tasks, match, mine, myId],
  );
  const count = (s: TaskStatus) => shown.filter((t) => t.status === s).length;
  const column = shown.filter((t) => t.status === col);

  return (
    <div className="stack">
      {me.manager && (
        <div className="row wrap" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNewTask(true)}>
              <Icon name="plus" size={16} />
              Задача
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShift(true)}>
              Задание на смену
            </button>
          </>
        </div>
      )}

      <div className="chips" role="group" aria-label="Чьи задачи">
        <button type="button" className="chip" aria-pressed={mine} onClick={() => (setMine(true), saveFilter('board-mine', '1'))}>
          Мои
        </button>
        <button type="button" className="chip" aria-pressed={!mine} onClick={() => (setMine(false), saveFilter('board-mine', '0'))}>
          Все
        </button>
      </div>
      {tasks && tasks.length > 0 && <AreaFilter areas={areas} used={tasks.map((t) => t.areaId)} value={area} onChange={setArea} />}

      <div className="seg seg-fit board-cols" role="tablist" aria-label="Колонки доски">
        {TASK_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={col === s}
            aria-pressed={col === s}
            onClick={() => {
              setCol(s);
              saveFilter('board-col', s);
            }}
          >
            {TASK_STATUS_LABEL[s]} {count(s) > 0 && <b className="col-count">{count(s)}</b>}
          </button>
        ))}
      </div>

      {!tasks && <div className="skeleton" />}
      {tasks && column.length === 0 && (
        <p className="muted small" style={{ textAlign: 'center' }}>
          {col === 'done' ? 'За неделю пока ничего не завершено.' : mine ? 'Здесь ваших задач нет.' : 'Здесь пусто.'}
          {me.manager && col !== 'done' ? ' Задачи появляются из задания на смену или кнопкой «Задача».' : ''}
        </p>
      )}
      <div className="stack" style={{ gap: 8 }}>
        {column.map((t) => (
          <TaskCard key={t.id} t={t} areas={areas} onOpen={() => setOpen(t.id)} />
        ))}
      </div>

      {open && <TaskSheet id={open} onClose={() => setOpen(null)} onChanged={load} />}
      {shift && (
        <ShiftSheet
          areas={areas}
          onClose={() => setShift(false)}
          onDone={async () => {
            setShift(false);
            setCol('assigned');
            await load();
          }}
        />
      )}
      {newTask && (
        <TaskEditor
          task={null}
          areas={areas}
          onClose={() => setNewTask(false)}
          onSaved={async () => {
            setNewTask(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function TaskCard({ t, areas, onOpen }: { t: TaskDto; areas: AreaDto[]; onOpen: () => void }) {
  const sh = shiftText(t);
  return (
    <button type="button" className={`task-card kind-${t.kind}${t.checkStatus === 'issues' ? ' issues' : ''}`} onClick={onOpen}>
      <span className="row" style={{ gap: 6 }}>
        <span className="task-kind">
          <Icon name={KIND_ICON[t.kind]} size={14} />
          {TASK_KIND_LABEL[t.kind]}
        </span>
        {t.checkStatus === 'issues' && (
          <span className="task-kind attention">
            <Icon name="alert" size={14} />
            замечания
          </span>
        )}
        {t.checkStatus === 'ok' && <span className="task-kind">всё в порядке</span>}
        <span className="grow" />
        {sh && <span className="small muted">{sh}</span>}
      </span>
      <span className="title">{t.title}</span>
      {(t.qty !== null || t.equipmentName) && (
        <span className="sub">
          {t.qty !== null ? `${t.qty.toLocaleString('ru-RU')} шт.` : ''}
          {t.qty !== null && t.equipmentName ? ' · ' : ''}
          {t.equipmentName ?? ''}
        </span>
      )}
      <span className="sub ellipsis">
        {t.assignees.length ? t.assignees.map((a) => a.name).join(', ') : 'не назначено'}
        {t.areaId ? ` · ${areaPath(areas, t.areaId)}` : ''}
      </span>
      {t.dueAt && <span className="sub">срок: {new Date(t.dueAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
    </button>
  );
}

export { plural, Seg };
